import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";

/**
 * Descarga un blob binario vía fetch nativo (NO ky).
 *
 * Por qué fetch nativo y no ky: ky maneja el body del response internamente
 * (para retries, afterResponse hooks, etc.) y eso puede corromper bytes
 * binarios como un PDF. El `Response.blob()` de fetch garantiza bytes intactos.
 *
 * Inyecta el Authorization header desde el auth store.
 */
export async function fetchBlob(url: string): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "same-origin",
  });
  if (!res.ok) {
    // Intenta parsear el error como JSON
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message ?? body?.detail ?? msg;
    } catch {
      /* no JSON body */
    }
    throw new Error(msg);
  }
  return await res.blob();
}

export type ConfigItem = {
  key: string;
  value: unknown;
  value_type: string;
  grupo: string;
  descripcion: string | null;
  editable_ui: boolean;
};

export function useConfigByGrupo(grupo: string) {
  return useQuery({
    queryKey: ["config", grupo],
    queryFn: () =>
      apiCall<ConfigItem[]>("get", "/api/v1/config", {
        searchParams: { grupo },
      }),
  });
}

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      key: string;
      value: unknown;
      value_type?: string;
      grupo?: string;
      descripcion?: string;
    }) => {
      const { key, ...body } = args;
      return apiCall<ConfigItem>(
        "put",
        `/api/v1/config/${encodeURIComponent(key)}`,
        { json: body },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["config"] });
    },
  });
}

/** Preview del PDF sin guardar la plantilla. Retorna el blob del PDF. */
export async function previewPlantilla(
  plantillaHtml: string,
  contratoId?: string,
): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch("/api/v1/config/preview-plantilla", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      plantilla_html: plantillaHtml,
      contrato_id: contratoId ?? null,
    }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message ?? msg;
    } catch {
      /* */
    }
    throw new Error(msg);
  }
  return await res.blob();
}

/** Preview del PDF de factura sin guardar la plantilla (F20.6). */
export async function previewPlantillaFactura(
  plantillaHtml: string,
  facturaId?: string,
): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch("/api/v1/config/preview-plantilla-factura", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      plantilla_html: plantillaHtml,
      factura_id: facturaId ?? null,
    }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message ?? msg;
    } catch {
      /* */
    }
    throw new Error(msg);
  }
  return await res.blob();
}

/** rc36: abre el recibo del pago en ventana nueva con auto-print.
 * Hace fetch con token, convierte a blob URL y abre — porque `window.open`
 * directo no manda el Authorization Bearer header.
 */
export async function abrirReciboPago(pagoId: string): Promise<void> {
  // rc134 — SEGURIDAD: antes se descargaba el HTML y se abría como blob
  // SAME-ORIGIN → el <script> de la plantilla (editable en Ajustes) corría
  // con acceso al localStorage del cajero (token). Ahora el API da un enlace
  // de 10 min y sirve el recibo con CSP `sandbox` (origen opaco: imprime,
  // pero no puede leer la sesión ni llamar al API como el usuario).
  // La ventana se abre ANTES del await para que Safari/iOS no la bloquee
  // como popup (solo permite window.open síncrono al clic).
  const win = window.open("", "_blank", "width=420,height=900");
  try {
    const { url } = await apiCall<{ url: string }>("post", `/api/v1/pagos/${pagoId}/recibo/enlace`, { json: {} });
    if (win) {
      win.location.href = url;
    } else {
      window.open(url, "_blank", "width=420,height=900");
    }
  } catch (e) {
    win?.close();
    throw e;
  }
}

/** rc36: Preview HTML del recibo de pago (miniprinter 80mm) sin guardar. */
export async function previewPlantillaRecibo(
  plantillaHtml: string,
  pagoId?: string,
): Promise<string> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch("/api/v1/config/preview-plantilla-recibo", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      plantilla_html: plantillaHtml,
      pago_id: pagoId ?? null,
    }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message ?? msg;
    } catch {
      /* */
    }
    throw new Error(msg);
  }
  return await res.text();
}

/** Descarga el PDF del contrato generado desde la plantilla. */
export async function descargarContratoPDF(contratoId: string): Promise<Blob> {
  return fetchBlob(`/api/v1/contratos/${contratoId}/documento.pdf`);
}

/** Descarga el PDF de la factura generado desde la plantilla (F20.5). */
export async function descargarFacturaPDF(facturaId: string): Promise<Blob> {
  return fetchBlob(`/api/v1/facturas/${facturaId}/documento.pdf`);
}

/** Descarga el PDF firmado (si existe). */
export async function descargarContratoFirmado(contratoId: string): Promise<Blob> {
  return fetchBlob(`/api/v1/contratos/${contratoId}/contrato-firmado.pdf`);
}

/** Upload del PDF firmado via multipart. */
export async function uploadContratoFirmado(
  contratoId: string, file: File,
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const form = new FormData();
  form.append("archivo", file);
  const res = await fetch(`/api/v1/contratos/${contratoId}/contrato-firmado`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message ?? msg;
    } catch {
      /* */
    }
    throw new Error(msg);
  }
}
